// Pricing section — three tiers (Free, Pro highlighted, Annual).
import { useT } from "../LangContext";
import { C } from "../../lib/colors";
import PromoBanner from "../PromoBanner";
import { getDiscount, BASE_PRICE, discountedPrice, savedAmount } from "../../lib/promo";

function PricingCard({
  tier, price, priceSub, features, ctaLabel, onCTAClick, featured,
  oldPrice, saveLabel, promoBadge,
}) {
  return (
    <div data-testid={`pricing-card-${tier}`} style={{
      background: featured ? `${C.brass}08` : C.archive,
      border: `1px solid ${featured ? C.brass : C.dust}`,
      padding: 28,
      display: "flex",
      flexDirection: "column",
      gap: 16,
      position: "relative",
    }}>
      {promoBadge && (
        <span data-testid="pricing-summer-badge" style={{
          position: "absolute", top: 12, left: 12,
          fontSize: 9, fontFamily: "var(--mono)", color: C.ink, background: C.brass,
          letterSpacing: "0.1em", padding: "3px 7px", textTransform: "uppercase",
        }}>
          {promoBadge}
        </span>
      )}
      {featured && (
        <span data-testid="pricing-recommended-badge" style={{
          position: "absolute", top: 12, right: 12,
          fontSize: 9, fontFamily: "var(--mono)", color: C.brass,
          letterSpacing: "0.12em", padding: "2px 6px",
          border: `1px solid ${C.brass}`,
        }}>
          ★ RECOMMENDED
        </span>
      )}
      <div style={{
        fontFamily: "var(--plex)", fontSize: 12, fontWeight: 600,
        letterSpacing: "0.18em", textTransform: "uppercase",
        color: featured ? C.brass : C.smoke,
        marginTop: promoBadge ? 14 : 0,
      }}>
        {tier}
      </div>
      <div>
        {oldPrice && (
          <div style={{
            fontFamily: "var(--mono)", fontSize: 15, color: C.smoke,
            textDecoration: "line-through", textDecorationColor: C.cinnabar, marginBottom: 2,
          }}>
            {oldPrice}
          </div>
        )}
        <div style={{
          fontFamily: "var(--fraunces)",
          fontVariationSettings: "'opsz' 144, 'WONK' 1",
          fontWeight: 900,
          fontSize: 40,
          color: C.bone,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}>
          {price}
        </div>
        {saveLabel && (
          <div style={{
            fontFamily: "var(--mono)", fontSize: 11, color: C.verdigris,
            marginTop: 4, letterSpacing: "0.02em",
          }}>
            {saveLabel}
          </div>
        )}
        <div style={{
          fontFamily: "var(--plex)",
          fontStyle: "italic",
          fontSize: 12,
          color: C.smoke,
          marginTop: 6,
        }}>
          {priceSub}
        </div>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0,
                   display: "flex", flexDirection: "column", gap: 8 }}>
        {features.map((f, i) => (
          <li key={i} style={{
            fontFamily: "var(--plex)",
            fontSize: 13,
            color: C.linen,
            display: "flex", gap: 10, alignItems: "flex-start",
          }}>
            <span style={{ color: C.brass, flexShrink: 0 }}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <button onClick={onCTAClick}
              data-testid={`pricing-cta-${tier}`}
              style={{
                marginTop: "auto",
                background: featured ? C.brass : "transparent",
                color: featured ? C.ink : C.bone,
                border: `1px solid ${featured ? C.brass : C.dust}`,
                padding: "12px 18px",
                fontFamily: "var(--plex)",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}>
        {ctaLabel}
      </button>
    </div>
  );
}

export default function LandingPricing({ onCTAClick }) {
  const { t, lang } = useT();
  const locale = lang === "es" ? "es-ES" : "en-US";

  // Promo props per plan (empty when the sale is off).
  const proPromo = getDiscount().active ? {
    price: `${discountedPrice(BASE_PRICE.pro, locale)}€`,
    oldPrice: "31€",
    promoBadge: t("promo_badge", { pct: getDiscount().pct }),
    saveLabel: `${t("promo_save", { amt: `${savedAmount(BASE_PRICE.pro, locale)}€` })} · ${t("promo_first_month")}`,
  } : { price: "31€" };

  const annualPromo = getDiscount().active ? {
    price: `${discountedPrice(BASE_PRICE.annual, locale)}€`,
    oldPrice: "199€",
    promoBadge: t("promo_badge", { pct: getDiscount().pct }),
    saveLabel: `${t("promo_save", { amt: `${savedAmount(BASE_PRICE.annual, locale)}€` })} · ${t("promo_first_year")}`,
  } : { price: "199€" };

  return (
    <section style={{ padding: "60px 0" }} data-testid="landing-pricing">
      <h2 style={{
        fontFamily: "var(--fraunces)",
        fontVariationSettings: "'opsz' 144, 'WONK' 1",
        fontWeight: 900,
        fontSize: 36,
        color: C.bone,
        letterSpacing: "-0.01em",
        margin: 0,
      }}>
        {t("landing_pricing_title")}
      </h2>
      <p style={{
        fontFamily: "var(--plex)",
        fontStyle: "italic",
        fontSize: 14,
        color: C.linen,
        margin: "10px 0 32px",
      }}>
        {t("landing_pricing_subtitle")}
      </p>
      <PromoBanner />
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
      }}>
        <PricingCard
          tier="free"
          price="0€"
          priceSub={t("landing_price_forever")}
          features={[
            t("landing_free_feat_1"),
            t("landing_free_feat_2"),
            t("landing_free_feat_3"),
          ]}
          ctaLabel={t("landing_free_cta")}
          onCTAClick={onCTAClick}
        />
        <PricingCard
          tier="pro"
          priceSub={t("landing_price_monthly")}
          features={[
            t("landing_pro_feat_1"),
            t("landing_pro_feat_2"),
            t("landing_pro_feat_3"),
            t("landing_pro_feat_4"),
            t("landing_pro_feat_5"),
            t("landing_pro_feat_6"),
          ]}
          ctaLabel={t("landing_pro_cta")}
          onCTAClick={onCTAClick}
          featured
          {...proPromo}
        />
        <PricingCard
          tier="annual"
          priceSub={t("landing_price_yearly")}
          features={[
            t("landing_annual_feat_1"),
            t("landing_annual_feat_2"),
            t("landing_annual_feat_3"),
          ]}
          ctaLabel={t("landing_annual_cta")}
          onCTAClick={onCTAClick}
          {...annualPromo}
        />
      </div>
    </section>
  );
}
